import React from 'react';
import { cn } from "@/lib/utils";
import { Loader2 } from 'lucide-react';

/**
 * NeuralPulseOrb — the precision "neural graph" mark used for ALL AI loading
 * states across CaraLoom. Per the Swiss-bank overhaul: NO mascot/face/brain.
 * A glowing cyan core with satellite nodes, pulsing links, and an expanding
 * pulse ring — clean, high-precision, institutional.
 */
const SATELLITES = [
  [14, 16], [50, 14], [11, 41], [53, 43], [32, 54],
];

export function NeuralPulseOrb({ size = 84, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label="AI processing"
    >
      <defs>
        <radialGradient id="np-core" cx="50%" cy="42%" r="60%">
          <stop offset="0" stopColor="#A5F3FC" />
          <stop offset="55%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#0E7490" />
        </radialGradient>
      </defs>

      {/* neural links */}
      {SATELLITES.map(([x, y], i) => (
        <line key={`l${i}`} x1="32" y1="32" x2={x} y2={y} stroke="#22D3EE" strokeWidth="1" strokeLinecap="round" opacity="0.55">
          <animate attributeName="opacity" values="0.2;0.75;0.2" dur="1.6s" begin={`${i * 0.18}s`} repeatCount="indefinite" />
        </line>
      ))}

      {/* satellite nodes */}
      {SATELLITES.map(([x, y], i) => (
        <circle key={`n${i}`} cx={x} cy={y} r="2.4" fill="#67E8F9">
          <animate attributeName="r" values="1.8;2.8;1.8" dur="1.6s" begin={`${i * 0.18}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;1;0.5" dur="1.6s" begin={`${i * 0.18}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* expanding pulse ring */}
      <circle cx="32" cy="32" r="7" fill="none" stroke="#22D3EE" strokeWidth="1.25" opacity="0.5">
        <animate attributeName="r" values="7;13;7" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="1.8s" repeatCount="indefinite" />
      </circle>

      {/* glowing core */}
      <circle cx="32" cy="32" r="6.4" fill="url(#np-core)">
        <animate attributeName="opacity" values="0.75;1;0.75" dur="1.2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// Primary AI loading state — Neural Pulse (kept name `AIBrainLoader` for callers).
export function AIBrainLoader({ className, message = "Processing with AI...", size = 84 }) {
  return (
    <div className={cn("flex flex-col items-center justify-center py-12", className)} data-testid="neural-pulse-loader">
      <NeuralPulseOrb size={size} />
      <p className="mt-6 text-sm font-semibold tracking-tight text-cn-text dark:text-slate-200 text-center">
        {message}
      </p>
      {/* scanning shimmer sweep */}
      <div className="mt-4 relative h-1 w-44 rounded-full bg-cn-border/60 dark:bg-cn-ink-700 overflow-hidden">
        <div className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-cn-accent to-transparent animate-shimmer-slide" />
      </div>
    </div>
  );
}

// Alias for clarity in new code.
export const NeuralPulseLoader = AIBrainLoader;

// Skeleton Card Loader
export function SkeletonCard({ className }) {
  return (
    <div className={cn("rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 p-6 animate-pulse", className)}>
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-slate-200 dark:bg-cn-ink-700 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-slate-200 dark:bg-cn-ink-700 rounded w-1/3" />
          <div className="h-3 bg-slate-200 dark:bg-cn-ink-700 rounded w-1/2" />
        </div>
      </div>
      <div className="space-y-3">
        <div className="h-3 bg-slate-200 dark:bg-cn-ink-700 rounded" />
        <div className="h-3 bg-slate-200 dark:bg-cn-ink-700 rounded w-5/6" />
        <div className="h-3 bg-slate-200 dark:bg-cn-ink-700 rounded w-4/6" />
      </div>
    </div>
  );
}

// Progress Bar with Steps
export function AIProgressLoader({
  className,
  steps = ["Analyzing data", "Processing with AI", "Generating insights"],
  currentStep = 0,
  progress = 0
}) {
  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      {/* Progress bar */}
      <div className="relative h-2 bg-slate-200 dark:bg-cn-ink-700 rounded-full overflow-hidden mb-4">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-cn-primary to-cn-accent rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => (
          <div
            key={index}
            className={cn(
              "flex items-center gap-3 text-sm transition-all duration-300",
              index === currentStep ? "text-cn-primary dark:text-cn-accent font-medium" :
              index < currentStep ? "text-cn-primary dark:text-cn-accent" :
              "text-slate-400 dark:text-slate-500"
            )}
          >
            {index < currentStep ? (
              <div className="w-5 h-5 bg-cn-primary rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : index === currentStep ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <div className="w-5 h-5 border-2 border-slate-300 dark:border-cn-ink-650 rounded-full" />
            )}
            {step}
          </div>
        ))}
      </div>
    </div>
  );
}

// Inline Spinner with Text
export function InlineLoader({ className, text = "Loading..." }) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="relative w-5 h-5">
        <div className="absolute inset-0 rounded-full border-2 border-cn-border" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-cn-accent animate-spin" />
      </div>
      <span className="text-sm text-slate-600 dark:text-slate-400">{text}</span>
    </div>
  );
}

// Pulse Card for AI Features — Neural Pulse, no mascot.
export function AIPulseCard({ className, title }) {
  return (
    <div className={cn(
      "relative overflow-hidden rounded-2xl border border-cn-border bg-cn-soft/50 dark:bg-cn-ink-800 p-8",
      className
    )}>
      <div className="relative flex flex-col items-center text-center">
        <NeuralPulseOrb size={56} className="mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          {title || "Processing..."}
        </h3>
        <div className="w-full max-w-xs space-y-2 mt-4">
          <div className="h-2 bg-slate-200 dark:bg-cn-ink-700 rounded-full overflow-hidden">
            <div className="h-full w-2/3 bg-gradient-to-r from-cn-primary to-cn-accent rounded-full animate-loading-bar" />
          </div>
          <div className="h-2 bg-slate-200 dark:bg-cn-ink-700 rounded-full overflow-hidden">
            <div className="h-full w-1/2 bg-gradient-to-r from-cn-primary to-cn-accent rounded-full animate-loading-bar" style={{ animationDelay: '0.2s' }} />
          </div>
          <div className="h-2 bg-slate-200 dark:bg-cn-ink-700 rounded-full overflow-hidden">
            <div className="h-full w-3/4 bg-gradient-to-r from-cn-primary to-cn-accent rounded-full animate-loading-bar" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Full Page Loading Overlay
export function AILoadingOverlay({
  isVisible,
  message = "AI is processing your request...",
  subMessage = "This may take a few seconds"
}) {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-cn-ink-800 border border-cn-border rounded-3xl p-8 max-w-sm mx-4 text-center">
        <AIBrainLoader message={message} />
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
          {subMessage}
        </p>
      </div>
    </div>
  );
}

// Typing animation for AI responses
export function AITypingIndicator({ className }) {
  return (
    <div className={cn("flex items-center gap-1 p-3 bg-slate-100 dark:bg-cn-ink-700 rounded-2xl rounded-tl-none w-fit", className)}>
      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

// AI Chat Loading Animation — Neural Pulse avatar, no mascot.
export function AIChatLoader({ className, message = "Thinking" }) {
  return (
    <div className={cn("flex items-start gap-3 max-w-[85%]", className)}>
      {/* Neural avatar */}
      <div className="relative flex-shrink-0 w-10 h-10 rounded-xl bg-cn-primary flex items-center justify-center">
        <NeuralPulseOrb size={26} />
      </div>

      {/* Message bubble */}
      <div className="bg-slate-100 dark:bg-cn-ink-800 border border-cn-border rounded-2xl rounded-tl-sm p-4">
        <div className="flex items-center gap-3">
          {/* Animated wave bars */}
          <div className="flex items-end gap-0.5 h-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-1 bg-gradient-to-t from-cn-primary to-cn-accent rounded-full animate-wave"
                style={{ animationDelay: `${i * 100}ms`, height: '100%' }}
              />
            ))}
          </div>
          <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">
            {message}
          </span>
        </div>
        {/* Shimmer line */}
        <div className="mt-3 h-1 bg-slate-200 dark:bg-cn-ink-700 rounded-full overflow-hidden">
          <div className="h-full w-1/2 bg-gradient-to-r from-transparent via-cn-accent to-transparent rounded-full animate-shimmer-slide" />
        </div>
      </div>
    </div>
  );
}
